import { getInsuranceProductLabel, isPassportCardInsurance } from '~/utils/insuranceUtils';

interface OrderSummaryItem {
  startDate: Date | null;
  endDate: Date | null;
  description: string;
}

/**
 * Extracts summary information from an order and returns an array of summary items
 * with start/end dates, descriptive information, and icons for each item type
 */
export const extractOrderSummary = (order: App.Order): Array<OrderSummaryItem> => {
  const summaryItems: Array<OrderSummaryItem> = [];

  // Bedbank items
  if (order.has_bedbank) {
    order.bedbank_items.forEach((bedbankItem) => {
      summaryItems.push({
        startDate: bedbankItem.check_in ? new Date(bedbankItem.check_in) : null,
        endDate: bedbankItem.check_out ? new Date(bedbankItem.check_out) : null,
        description: bedbankItem.offer?.name,
      });
    });
  }

  // Accommodation items
  if (order.accommodation_items.length > 0) {
    order.accommodation_items.forEach((accommodationItem) => {
      const checkIn = accommodationItem.reservation?.check_in || accommodationItem.reservation?.start_date;
      const checkOut = accommodationItem.reservation?.check_out || accommodationItem.reservation?.end_date;

      summaryItems.push({
        startDate: checkIn ? new Date(checkIn) : null,
        endDate: checkOut ? new Date(checkOut) : null,
        description: accommodationItem.offer?.location,
      });
    });
  }

  // Tour items
  if (order.has_tour) {
    order.tour_items.forEach((tourItem) => {
      summaryItems.push({
        startDate: tourItem.start_date ? new Date(tourItem.start_date) : null,
        endDate: tourItem.end_date ? new Date(tourItem.end_date) : null,
        description: tourItem.tour_name,
      });
    });
  }

  // Cruise items
  if (order.cruise_items.length > 0) {
    order.cruise_items.forEach((cruiseItem) => {
      summaryItems.push({
        startDate: cruiseItem.departure_date ? new Date(cruiseItem.departure_date) : null,
        endDate: cruiseItem.return_date ? new Date(cruiseItem.return_date) : null,
        description: cruiseItem.offer_name,
      });
    });
  }

  // Flight items
  if (order.has_flight) {
    order.flight_items.forEach((flightItem) => {
      if (flightItem.journey?.departing?.flights?.length) {
        const departingDate = new Date(
          Math.min(...flightItem.journey.departing.flights.map((flight) => new Date(flight.departing_date).getTime())),
        );

        const returningDate = flightItem.journey.returning?.flights?.length
          ? new Date(
              Math.max(
                ...flightItem.journey.returning.flights.map((flight) => new Date(flight.departing_date).getTime()),
              ),
            )
          : null;

        // Get departure and arrival city names from the first departing flight
        const firstDepartingFlight = flightItem.journey.departing.flights[0] as App.Flight & {
          departing_display_names?: { primary?: string };
          arrival_display_names?: { primary?: string };
        };
        const departureCity = firstDepartingFlight?.departing_display_names?.primary || 'Unknown';
        const arrivalCity = firstDepartingFlight?.arrival_display_names?.primary || 'Unknown';

        summaryItems.push({
          startDate: departingDate,
          endDate: returningDate,
          description: `${departureCity} → ${arrivalCity}`,
        });
      }
    });
  }

  // Experience items
  if (order.has_experience) {
    order.experience_items.forEach((experienceItem) => {
      if (!experienceItem.complimentary) {
        summaryItems.push({
          startDate: experienceItem.ticket?.date ? new Date(experienceItem.ticket.date) : null,
          endDate: null,
          description: experienceItem.title,
        });
      }
    });
  }

  // Insurance items
  if (order.has_insurance) {
    order.insurance_items.forEach((insuranceItem) => {
      summaryItems.push({
        startDate: insuranceItem.start_date ? new Date(insuranceItem.start_date) : null,
        endDate: insuranceItem.end_date ? new Date(insuranceItem.end_date) : null,
        // PassportCard's label already reads as "... Insurance"; other providers keep the
        // "Insurance <code>" wording.
        description: isPassportCardInsurance(insuranceItem)
          ? getInsuranceProductLabel(insuranceItem)
          : `Insurance ${insuranceItem.product_id || 'Policy'}`,
      });
    });
  }

  // Car hire items
  if (order.car_hire_items.length > 0) {
    order.car_hire_items.forEach((carHireItem) => {
      const pickUpDate = carHireItem.reservation?.pickUp?.date;
      const returnDate = carHireItem.reservation?.return?.date;

      summaryItems.push({
        startDate: pickUpDate && typeof pickUpDate === 'string' ? new Date(pickUpDate) : null,
        endDate: returnDate && typeof returnDate === 'string' ? new Date(returnDate) : null,
        description: `Car Hire ${carHireItem.reservation?.providerReservationCode || 'Booking'}`,
      });
    });
  }

  // Custom offer items
  if (order.custom_offer_items.length > 0) {
    order.custom_offer_items.forEach((customOfferItem) => {
      const customItem = customOfferItem.custom_offer?.items?.[0];
      if (customItem) {
        summaryItems.push({
          startDate: customItem.start_date ? new Date(customItem.start_date) : null,
          endDate: customItem.end_date ? new Date(customItem.end_date) : null,
          description: customItem.name,
        });
      }
    });
  }

  return summaryItems;
};

export default extractOrderSummary;
